import React, { useEffect, useState } from "react";
import { Button, Container, Input, Spacer, VDBText } from "@components/atoms";
import { Controller, useForm } from "react-hook-form";
import { schema } from "@components/molecules/ContactUs/utils/formSchema";
import { yupResolver } from "@hookform/resolvers/yup";
import { ButtonCategory, ButtonType } from "@components/atoms/Button/types";
import styled, { useTheme } from "styled-components/native";
import { ContactUsFormProps, ContactUsProps } from "./types";
import Modal from "@components/atoms/Modal";
import KeyboardAwareSlideSheet from "@components/atoms/KeyboardAwareSlideSheet";
import { Keyboard, Platform } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { useContactUs, usePublicContactUs } from "@apis/contactUs";
import { useTranslation } from "react-i18next";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { css } from "styled-components/native";
import { useUserAnalytics } from "../../../hooks/useAnalytics";
import { ContactUsEventType } from "@utils/analytics.web";
import { useSelector } from "react-redux";
import CountryCodeDropdownPicker from "@components/atoms/CountryCodePickerWeb";
import { RootState } from "@redux/store";
import { DefaultTheme } from "styled-components/native";
import CountryCodePicker from "@components/atoms/CountryPicker";

const ContactUsForm = ({
  onClose,
  screenName,
  onResponseDataChange,
  showSuccess,
  subjectPrefix,
  prefill,
}: ContactUsFormProps) => {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const analytics = useUserAnalytics();
  const trackWebEvent = analytics?.trackWebEvent ?? undefined;
  const typography = useTypographyStore();
  const userInfo = useUserInfo();
  const { t } = useTranslation();
  const isGuest = useSelector((state: RootState) => state.userInfo.isGuest);
  const hasSessionUser = Boolean(userInfo.user?.token) && !isGuest;
  const [countryCode, setCountyCode] = useState(prefill?.countryCode ?? "");
  const [openDrop, setOpenDropDown] = useState(false);
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm({
    resolver: yupResolver(schema(t)),
    defaultValues: hasSessionUser
      ? {
          username: userInfo?.user.username,
          email: userInfo?.user.email,
          phoneNumber: userInfo?.user.phone_number,
        }
      : {
          username: prefill?.username ?? "",
          email: prefill?.email ?? "",
          phoneNumber: prefill?.phoneNumber ?? "",
        },
  });
  const { settings } = useOrgSettingsSlice();
  const contactUs = useContactUs();
  const [commentText, setCommentText] = useState<string>("");
  const publicContactUs = usePublicContactUs();
  const subject_prefix = subjectPrefix
    ? t(subjectPrefix, {
        defaultValue: "Feedback",
      })
    : undefined;

  const handleEvent = (data: any) => {
    if (userInfo.user.token && !isGuest) {
      const payload: any = {
        user_comment: data.comments,
        screen_name: screenName,
      };

      if (subject_prefix) {
        payload.subject_prefix = subject_prefix;
      }
      contactUs.mutate(payload, {
        onSuccess(onSuccessData: any) {
          if (onSuccessData)
            if (onResponseDataChange) {
              onResponseDataChange(onSuccessData?.data);
              if (trackWebEvent) {
                trackWebEvent(ContactUsEventType.SUBMIT_CONTACT_US_SUCCESS, {});
              }
            }
          onClose();
          if (showSuccess) {
            showSuccess();
          }
        },
        onError() {
          if (trackWebEvent) {
            trackWebEvent(ContactUsEventType.SUBMIT_CONTACT_US_ERROR, {});
          }
        },
      });
    } else {
      publicContactUs.mutate(
        {
          name: data.username,
          email: data.email,
          phone_number_country_code: countryCode,
          phone_number: data.phoneNumber,
          user_comment: data.comments,
          org_id: settings?.organization?.id,
          screen_name: screenName,
        },
        {
          onSuccess(onSuccessData: any) {
            if (onSuccessData)
              if (onResponseDataChange) {
                onResponseDataChange(onSuccessData?.data);
              }
            onClose();
            if (showSuccess) {
              showSuccess();
            }
          },
        }
      );
    }
  };

  const handleCountrySelect = () => {
    setOpenDropDown(false);
  };

  useEffect(() => {
    if (trackWebEvent && userInfo.user?.id && !isGuest) {
      trackWebEvent(ContactUsEventType.CLICK_CONTACT_US, {});
    }
  }, []);

  return (
    <>
      <Spacer size={SpaceType.Double} />
      <Container>
        <VDBText color={theme["core-05"]} style={typography["Body-R"]}>
          {t("contactUs.screenText.header.text")}
        </VDBText>
        <Spacer size={SpaceType.Big} />
        <Controller
          control={control}
          render={({
            field: { onChange, onBlur, value },
            formState: { errors },
          }) => (
            <Input
              inputLabel={t("contactUs.input.username.inputLabel.text")}
              leftIcon={undefined}
              hideIconContainer={true}
              placeholder={t("contactUs.input.username.placeholder.text")}
              onBlur={() => {
                onBlur();
                if (!value) {
                  control.setError("username", {
                    type: "required",
                    message: t("contactUs.input.validation.required.text"),
                  });
                }
              }}
              onChangeText={(text) => {
                onChange(text);
                if (text.trim() !== "") {
                  control.setError("username", {
                    type: "required",
                    message: t(""),
                  });
                }
              }}
              value={value}
              error={errors?.username?.message}
              disabled={Boolean(
                (userInfo?.user.username && !isGuest) || prefill?.username
              )}
            />
          )}
          name="username"
        />
        <Spacer size={SpaceType.Big} />
        <Controller
          control={control}
          render={({
            field: { onChange, onBlur, value },
            formState: { errors },
          }) => (
            <Input
              inputLabel={t("contactUs.input.email.inputLabel.text")}
              leftIcon={undefined}
              hideIconContainer={true}
              placeholder={t("contactUs.input.email.placeholder.text")}
              onBlur={() => {
                onBlur();
                if (!value) {
                  control.setError("email", {
                    type: "required",
                    message: t("contactUs.input.validation.required.text"),
                  });
                } else {
                  const regex =
                    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                  if (!regex.test(value)) {
                    control.setError("email", {
                      type: "pattern",
                      message: t("contactUs.input.validation.email.text"),
                    });
                  } else {
                    control.setError("email", {
                      type: "required",
                      message: t(""),
                    });
                  }
                }
              }}
              onChangeText={(text) => {
                onChange(text);
              }}
              value={value}
              error={errors?.email?.message}
              disabled={Boolean(
                (userInfo?.user.email && !isGuest) || prefill?.email
              )}
              inputType={
                Platform.OS === "android" ? "default" : "email-address"
              }
              autoCorrect={false}
              autoComplete="off"
              textContentType={
                Platform.OS === "android" ? "none" : "emailAddress"
              }
              importantForAutofill="no"
              multiline={false}
            />
          )}
          name="email"
        />
        <Spacer size={SpaceType.Big} />
        <ContactNumber>
          <CountryCodeView isOpen={openDrop}>
            {theme.isLargeScreen ? (
              <CountryCodeDropdownPicker
                selected={countryCode}
                setSelected={(value) => {
                  setCountyCode(value);
                }}
                setCountryDetails={handleCountrySelect}
                pickerIsOpen={setOpenDropDown}
                isGuest={isGuest}
                disabled={Boolean(prefill?.countryCode)}
              />
            ) : (
              <CountryCodePicker
                onCountrySelect={(callingCode: string) => {
                  setCountyCode(String(callingCode));
                }}
                isGuest={isGuest}
                selected={countryCode}
                disabled={Boolean(prefill?.countryCode)}
              />
            )}
          </CountryCodeView>
          {!openDrop && (
            <>
              <Spacer size={SpaceType.Basic} />
              <PhoneNumberView>
                <Controller
                  control={control}
                  render={({
                    field: { onChange, onBlur, value },
                    formState: { errors },
                  }) => (
                    <Input
                      inputLabel={t(
                        "contactUs.input.phoneNumber.inputLabel.text"
                      )}
                      placeholder={t(
                        "contactUs.input.phoneNumber.placeholder.text"
                      )}
                      leftIcon={undefined}
                      hideIconContainer={true}
                      onBlur={() => {
                        onBlur();
                        if (!value) {
                          control.setError("phoneNumber", {
                            type: "required",
                            message: t(
                              "contactUs.input.validation.required.text"
                            ),
                          });
                        }
                      }}
                      onChangeText={(text) => {
                        const formattedText = text.replace(/[^0-9+]/g, "");

                        const validText = formattedText.replace(
                          /\+/g,
                          (match, offset) => (offset === 0 ? match : "")
                        );
                        onChange(validText);
                        if (validText.trim() !== "") {
                          control.setError("phoneNumber", {
                            type: "required",
                            message: t(""),
                          });
                        }
                      }}
                      value={value}
                      error={errors?.phoneNumber?.message}
                      disabled={Boolean(
                        (userInfo?.user.phone_number && !isGuest) ||
                          prefill?.phoneNumber
                      )}
                      keyboardType="number-pad"
                    />
                  )}
                  name="phoneNumber"
                />
              </PhoneNumberView>
            </>
          )}
        </ContactNumber>
        <Spacer size={SpaceType.Big} />
        <Controller
          control={control}
          render={({
            field: { onChange, onBlur, value },
            formState: { errors },
          }) => (
            <Input
              inputLabel={t("contactUs.input.comments.inputLabel.text")}
              placeholder={t("contactUs.input.comments.placeholder.text")}
              leftIcon={undefined}
              hideIconContainer={true}
              onBlur={() => {
                onBlur();
                if (!value) {
                  control.setError("comments", {
                    type: "required",
                    message: t("contactUs.input.validation.required.text"),
                  });
                }
              }}
              onChangeText={(text) => {
                const trimmedText = text.replace(/^[ \t]+/, ""); // Remove leading spaces and tabs, preserve new lines
                onChange(trimmedText);
                setCommentText(trimmedText);
                if (trimmedText.trim() !== "") {
                  control.setError("comments", {
                    type: "required",
                    message: t(""),
                  });
                }
              }}
              value={value}
              error={errors?.comments?.message}
              isMultilineTopPadding={commentText === "" ? false : true}
              multiline={true}
              {...(!theme.isLargeScreen && {
                blurOnSubmit: true,
                onSubmitEditing: () => {
                  Keyboard.dismiss();
                },
              })}
              keyboardType="default"
              returnKeyType="done"
            />
          )}
          name="comments"
        />
        <Spacer size={SpaceType.Basic} />
        <VDBText>{t("contactUs.screenText.footer.text")}</VDBText>
      </Container>
      <ButtonContainer insets={insets}>
        <ContainerOverlay>
          <Button
            type={ButtonType.primary}
            category={ButtonCategory.default}
            title={t("contactUs.button.send.text")}
            onPress={handleSubmit(handleEvent)}
            loading={contactUs.isLoading || publicContactUs.isLoading}
            disabled={
              contactUs.isLoading || publicContactUs.isLoading || !isValid
            }
          />
        </ContainerOverlay>
      </ButtonContainer>
    </>
  );
};
const ContactUsModal = ({
  isOpen,
  onClose,
  screenName,
  onResponseDataChange,
  showSuccess,
  subjectPrefix,
  prefill,
}: ContactUsProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const form = (
    <ContactUsForm
      onClose={onClose}
      screenName={screenName}
      onResponseDataChange={onResponseDataChange}
      showSuccess={showSuccess}
      subjectPrefix={subjectPrefix}
      prefill={prefill}
    />
  );

  const useKeyboardAwareSheet = Platform.OS !== "web" && theme.isTablet;

  if (useKeyboardAwareSheet) {
    return (
      <KeyboardAwareSlideSheet
        isOpen={isOpen}
        onClose={onClose}
        paddingLeft={0}
        paddingRight={0}
        paddingBottom={0}
        paddingTop={0}
        headerTitle={t("contactUs.title.text")}
      >
        {form}
      </KeyboardAwareSlideSheet>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      paddingLeft={0}
      paddingRight={0}
      paddingBottom={0}
      paddingTop={0}
      headerTitle={t("contactUs.title.text")}
      fitContent={true}
      centerTitle
    >
      {form}
    </Modal>
  );
};

const ContactNumber = styled.View`
  flex-direction: row;
  align-items: flex-start;
  width: 100%;
`;
const CountryCodeView = styled.View<{
  isOpen: boolean;
}>`
  justify-content: flex-start;
  ${({ isOpen, theme }: { isOpen: boolean; theme: DefaultTheme }) =>
    isOpen &&
    theme.isLargeScreen &&
    css`
      flex: 1;
    `}
`;

const PhoneNumberView = styled.View`
  flex: 1;
  justify-content: flex-start;
  margin-left: 8px;
`;

const ButtonContainer = styled.View<{
  insets?: { bottom: number };
}>`
  padding-top: ${SpaceType.Big}px;
  padding-bottom: ${({
    theme,
    insets,
  }: {
    theme: DefaultTheme;
    insets?: { bottom: number };
  }) =>
    !theme.isMobile
      ? `${SpaceType.ExtraLarge}px`
      : `${
          SpaceType.ExtraLarge +
          (Platform.OS === "android"
            ? insets && insets.bottom > 0
              ? insets.bottom
              : SpaceType.Double
            : 0)
        }px`};
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
  padding-horizontal: ${SpaceType.Double}px;
  align-items: center;
  ${({ theme }: { theme: DefaultTheme }) =>
    !theme.isMobile &&
    css`
      padding-horizontal: 0px;
    `}
`;

const ContainerOverlay = styled.View`
  width: 100%;
  ${({ theme }: { theme: DefaultTheme }) =>
    !theme.isMobile &&
    css`
      max-width: 608px;
    `}
`;

export default ContactUsModal;
